/**
 * Sub Nav
 */
import { registerComponent, Component } from 'js/component.js';
import smoothScroll from 'js/scrollto.js';
import {
    getViewportSize,
    isEditModeFooter,
    isIE11,
    throttle
} from 'js/helpers.js';
const componentName = 'sub-nav';

export class SubNav extends Component {
    /**
     * Component Setup
     */
    // Elements
    elements = {
        body: document.body,
        alertBanner: () => document.querySelector('.alert-banner'),
        globalNav: document.querySelector('header.global-nav'),
        container: this.element.querySelector(`.${componentName}__container`),
        ghost: document.querySelector(`.${componentName}__ghost`),
        position: document.querySelector(`.${componentName}__position`),
        subnavLinks: this.element.querySelectorAll(`.${componentName} a`),
        level2Menus: this.element.getElementsByClassName(`${componentName}__level-2`),
        level2Buttons: this.element.getElementsByClassName(`${componentName}__level-2_button`),
        level2Lis: this.element.getElementsByClassName(`${componentName}__level-2_li`),
        level2ViewAll: this.element.querySelector(`.${componentName}__level-2-view-all`),
        level3Menus: this.element.getElementsByClassName(`${componentName}__level-3`),
        level3Buttons: this.element.getElementsByClassName(`${componentName}__level-3_button`),
        level3Lis: this.element.getElementsByClassName(`${componentName}__level-3_li`),
        level4Menus: this.element.getElementsByClassName(`${componentName}__level-4`),
        level4Lis: this.element.getElementsByClassName(`${componentName}__level-4_li`),
        mobileTrigger: this.element.querySelector(`.${componentName}__mobile-trigger`)
    };

    state = {
        buttonsWrapped: false,
    };

    /**
     * Set level3 item offset for the CSS variable --subnav-level-3-offset
     * @param e
     */
    setSubnavLevel3Offset = (e) => {
        const level2 = e.target.closest('.sub-nav__level-2_li');
        const offset = level2.getBoundingClientRect().right;

        const lastLevel3 = level2.querySelector('.sub-nav__level-3');

        // If level3 menu exceeds the current viewport, right align the menu with the offset.
        if (lastLevel3.getBoundingClientRect().right > window.innerWidth) {
            lastLevel3.classList.add('align-right');
            this.elements.container.style.setProperty('--subnav-level-3-offset', `${offset}px`);
        }
    };

    /**
     * Get Top Offset of Element
     */
    getTopOffset = (el) => {
        if(!el) return 0;
        const rect = el.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        return rect.top + scrollTop;
    };

    // Util & State
    resizeThrottleMs = 10;
    scrollThrottleMs = 10;
    mobileAnimLength = 75;
    desktopMaxLinksPerColumn = 7;
    desktopLinkWidth = 200; // IE11 needs this
    currentViewport = getViewportSize(true);
    currentWindowWidth = window.innerWidth;
    currentScroll = window.pageYOffset;
    subnavHeight = this.element.offsetHeight;
    navHeight = this.elements.globalNav ? this.elements.globalNav.offsetHeight : 0;
    subnavTopPos = this.getTopOffset(this.element);

    /**
     * Adjust Z indexes for nav
     */
    setLowZindex = () => {
        if(this.currentViewport === 'desktop') this.element.style.zIndex = '1';
    };

    removeZindex = () => {
        if(this.currentViewport === 'desktop') this.element.style.zIndex = '';
    };

    /**
     * Create Ghost Elements
     * - position tracks the original position of the non-sticky subnav
     * - ghost maintains the height of the subnav when it's fixed (so page doesn't jump)
     */
    createSubNavGhost = () => {
        const positionEle = document.createElement('div');
        positionEle.classList.add(`${componentName}__position`);
        positionEle.classList.add('aem-GridColumn');

        const ghostEle = document.createElement('div');
        ghostEle.classList.add(`${componentName}__ghost`);
        ghostEle.classList.add('aem-GridColumn');
        ghostEle.style.cssText = `position:relative;height:${this.subnavHeight}px;width:100%;`;

        const parentEle = this.element.parentNode;
        parentEle.insertBefore(positionEle, this.element);
        parentEle.insertBefore(ghostEle, this.element);
        this.elements.position = positionEle;
        this.elements.ghost = ghostEle;
    };

    /**
     * Update Nav Height
     */
    updatePositions = () => {
        this.subnavHeight = this.element.offsetHeight;
        this.subnavTopPos = this.getTopOffset(this.elements.position);
        return true;
    };

    /**
     * Update Current Page Link
     */
    updateCurrentPageLink = () => {
        const currentPage = window.location.pathname;
        [...this.elements.subnavLinks].map((link) => {
            const href = link.getAttribute('href');
            const isButton = link.parentElement.classList.contains('sub-nav__button');
            if(href && href === currentPage && !isButton) {
                link.classList.add('current-page');
                link.setAttribute('tabindex', '-1');
            }
            return true;
        });
    };

    /**
     * Toggle Mobile Subnav
     */
    toggleMobileSubNav = () => {
        if(this.elements.mobileTrigger.getAttribute('aria-expanded') === 'false') {
            this.elements.mobileTrigger.setAttribute('aria-expanded', true);
            [...this.elements.level2Menus].map((ele) => ele.setAttribute('aria-hidden', 'false'));
            [...this.elements.level2Lis].map((ele) => ele.classList.remove('hidden'));
            [...this.elements.level2Buttons].map((ele) => ele.classList.remove('hidden'));
            [...this.elements.level2Lis].map((ele) => ele.classList.remove('active'));
            [...this.elements.level3Lis].map((ele) => ele.classList.remove('hidden'));
            [...this.elements.level3Lis].map((ele) => ele.classList.remove('active'));
        } else {
            this.elements.mobileTrigger.setAttribute('aria-expanded', false);
            this.closeAll();
        }
    };

    /**
     * Close All Menus
     */
    closeAll = () => {
        [...this.elements.level2Buttons].map((ele) => ele.setAttribute('aria-expanded', 'false'));
        [...this.elements.level3Buttons].map((ele) => ele.setAttribute('aria-expanded', 'false'));
        [...this.elements.level3Menus].forEach((ele) => { ele.setAttribute('aria-hidden', 'true'); ele.classList.remove('align-right'); });

        if(this.currentViewport === 'mobile') {
            [...this.elements.level2Menus].map((ele) => ele.setAttribute('aria-hidden', 'true'));
            [...this.elements.level2Lis].map((ele) => ele.classList.remove('active'));
        } else {
            [...this.elements.level2Menus].map((ele) => ele.setAttribute('aria-hidden', 'false'));
            [...this.elements.level2Lis].map((ele) => ele.classList.remove('hidden'));
        }
    };

    /**
     * Animate Mobile Opacity
     * @param {Function} callback - callback function to execute
     */
    animateMobileOpacity = (callback) => {
        if(typeof callback !== 'function') return;
        [...this.elements.level2Menus].map((ele) => ele.classList.add('animating'));

        setTimeout(() => {
            callback();
            [...this.elements.level2Menus].map((ele) => ele.classList.remove('animating'));
        }, this.mobileAnimLength);
    };

    /**
     * Update Menu Sizes
     */
    updateMenuSizes = () => {
        // Skip for templatized subnav
        if (this.elements.container.closest('ra-subnav') !== null) {
            return;
        }

        // Get a chunked Array for max links per column
        const getChunkedArrFor = (liElements) => {
            const finalArr = [];
            let currentChunkStart = 0;
            let currentChunk = -1;

            liElements.forEach((el, index) => {
                const hasTitle = el.querySelector(`.${componentName}__level-3_link-title`) !== null;
                const isNextChunk = (hasTitle || (index - currentChunkStart) === this.desktopMaxLinksPerColumn || index === 0);

                if(isNextChunk) {
                    finalArr.push([el]);
                    currentChunk += 1;
                    currentChunkStart = index;
                } else {
                    finalArr[currentChunk].push(el);
                }
            });

            return finalArr;
        };

        // Function to inject + populate a wrapper with single array of elements
        const populateWrapper = (childrenArray) => {
            if(!childrenArray || childrenArray.length < 0) return;

            const wrapperLiEle = document.createElement('li');
            const wrapperUlEle = document.createElement('ul');
            wrapperLiEle.classList.add('desktop-size-li');
            wrapperUlEle.classList.add('desktop-size-ul');

            wrapperLiEle.appendChild(wrapperUlEle);
            childrenArray[0].parentNode.insertBefore(wrapperLiEle, childrenArray[0]);

            for(let i = 0; i < childrenArray.length; i += 1) {
                const titleSpanEle = childrenArray[i].querySelector(`.${componentName}__level-3_link-title`);
                if(titleSpanEle) {
                    const newLiEle = document.createElement('li');
                    newLiEle.classList.add(`${componentName}__level-3_li`);
                    newLiEle.classList.add('desktop-li');
                    newLiEle.appendChild(titleSpanEle);
                    wrapperUlEle.appendChild(newLiEle);
                } else {
                    wrapperUlEle.appendChild(childrenArray[i]);
                }
            }
        };

        // Find all L3 Menus and inject our custom structure
        [...this.elements.level3Menus].map((l3MenuEle) => {
            const liElements = l3MenuEle.querySelectorAll('li.desktop-li');
            const viewAllLi = l3MenuEle.querySelector('li.view-all-li');
            let viewAllLiClone;

            if(viewAllLi) {
                viewAllLiClone = viewAllLi.cloneNode(true);
                viewAllLiClone.classList.add('desktop-view-all');
                viewAllLi.classList.add('mobile-li');
                viewAllLi.parentNode.prepend(viewAllLiClone);
            }

            if(liElements && liElements.length > -1) {
                const elementsArr = [...liElements];
                if(viewAllLi) elementsArr.unshift(viewAllLiClone);
                const liChunkArr = getChunkedArrFor(elementsArr);
                liChunkArr.map((arr) => populateWrapper(arr));
            }
            return true;
        });
    };

    /**
     * On L2 Button Click
     */
    onL2ButtonClick = (e) => {
        const parentEle = e.currentTarget;
        const l2ParentMenu = parentEle.parentNode.parentNode;
        const l2ParentLi = parentEle.parentNode;
        const l3ChildMenu = parentEle.parentNode.querySelector(`.${componentName}__level-3`);
        const l4ChildMenu = parentEle.parentNode.querySelector(`.${componentName}__level-4`);

        if(!l3ChildMenu) return false;
        const isExpanded = parentEle.getAttribute('aria-expanded') === 'true';

        // Desktop
        if(this.currentViewport === 'desktop') {
            if(isExpanded) {
                parentEle.setAttribute('aria-expanded', 'false');
                l3ChildMenu.setAttribute('aria-hidden', 'true');
            } else {
                this.closeAll();
                parentEle.setAttribute('aria-expanded', 'true');
                l3ChildMenu.setAttribute('aria-hidden', 'false');

                // IE11 Needs Explicit Width
                if(isIE11()) {
                    const liSizerEles = l3ChildMenu.querySelectorAll('.desktop-size-li');
                    if(liSizerEles.length > 0) l3ChildMenu.style.width = `${(liSizerEles.length * this.desktopLinkWidth) + 20}px`;
                }
                this.setSubnavLevel3Offset(e);
            }
        }

        if(this.currentViewport === 'mobile') {
            // Mobile
            this.animateMobileOpacity(() => {
                if(isExpanded) {
                    parentEle.setAttribute('aria-expanded', 'false');
                    l3ChildMenu.setAttribute('aria-hidden', 'true');
                    l2ParentLi.classList.remove('active');
                    [...this.elements.level2Lis].map((ele) => ele.classList.remove('hidden'));
                    this.elements.level2ViewAll.style.display = 'flex';
                } else {
                    this.closeAll();
                    l2ParentMenu.setAttribute('aria-hidden', 'false');
                    l2ParentLi.classList.remove('hidden');
                    l2ParentLi.classList.add('active');
                    parentEle.setAttribute('aria-expanded', 'true');
                    l3ChildMenu.setAttribute('aria-hidden', 'false');
                    this.elements.level2ViewAll.style.display = 'none';
                    [...this.elements.level3Lis].map((ele) => ele.classList.remove('hidden'));
                }
            });
        } else {
            // Desktop
            if(!l4ChildMenu) return true;
            l4ChildMenu.setAttribute('aria-hidden', 'false');
        }

        return true;
    };

    /**
     * On L3 Button Click (Mobile)
     */
    onL3ButtonClick = (e) => {
        const buttonEle = e.currentTarget;
        const parentL3Menu = buttonEle.parentNode.parentNode;
        const l3ParentLi = buttonEle.parentNode;
        const parentL2Menu = this.element.querySelector(`.${componentName}__level-2[aria-hidden="false"]`);
        const parentL2Li = buttonEle.parentNode.parentNode.parentNode;
        const parentL2Button = this.element.querySelector(`.${componentName}__level-2_button[aria-expanded="true"]`);
        const childL4Menu = l3ParentLi.querySelector(`.${componentName}__level-4`);
        const isExpanded = buttonEle.getAttribute('aria-expanded') === 'true';

        this.animateMobileOpacity(() => {
            if(isExpanded) {
                buttonEle.setAttribute('aria-expanded', 'false');
                l3ParentLi.classList.remove('active');
                childL4Menu.setAttribute('aria-hidden', 'true');
                parentL2Li.classList.add('active');
                parentL2Button.classList.remove('hidden');
                [...this.elements.level3Lis].map((ele) => ele.classList.remove('hidden'));
            } else {
                parentL2Menu.setAttribute('aria-hidden', 'false');
                parentL2Button.setAttribute('aria-expanded', 'true');
                parentL2Li.classList.remove('hidden');
                buttonEle.setAttribute('aria-expanded', 'true');
                parentL3Menu.setAttribute('aria-hidden', 'false');
                l3ParentLi.classList.remove('hidden');
                l3ParentLi.classList.add('active');
                childL4Menu.setAttribute('aria-hidden', 'false');
                [...this.elements.level4Lis].map((ele) => ele.classList.remove('hidden'));
            }
        });
    };

    /**
     * On Click Off
     */
    onClickOff = (e) => {
        if(!e.target.closest(`.${componentName}`) && !e.target.closest(`.${componentName}__mobile-trigger`)) {
            if(this.currentViewport === 'mobile' && this.elements.mobileTrigger.getAttribute('aria-expanded') === 'true') this.toggleMobileSubNav();
            this.closeAll();
        }
    };

    /**
     * Scroll to the in-page link
     * - since we have a sticky subnav on scroll + potential alert banner, we
     *   need to make sure that offsets are defined and used when scrolling the user
     */
    scrollToInPageLink = (e) => {
        const hrefValue = e.currentTarget.getAttribute('href');
        const isHrefHash = /^.*#.+$/.test(hrefValue);

        if(isHrefHash) {
            const hashEle = document.getElementById(hrefValue.replace(/^.*#(.+)$/, '$1'));
            if(!hashEle) return true;
            e.preventDefault();

            // Offsets
            const hashEleRect = hashEle.getBoundingClientRect();
            const hashYPos = hashEleRect.top + (window.pageYOffset || document.documentElement.scrollTop);
            const bannerOffset = this.elements.alertBanner() && (this.elements.alertBanner().getAttribute('aria-hidden') === 'false') ? this.elements.alertBanner().offsetHeight : 0;
            const defaultOffset = 15;

            // Scroll the user (smooth scroll on desktop)
            const finalScrollPos = hashYPos - (bannerOffset + this.element.offsetHeight + defaultOffset);
            if(this.currentViewport === 'mobile') {
                window.scrollTo(0, finalScrollPos);
                this.closeAll();
                this.elements.mobileTrigger.setAttribute('aria-expanded', false);
            } else {
                smoothScroll(finalScrollPos, null, 1000);
            }
        }

        return true;
    };

    /**
     * Adjust button wrapping
     */
    wrapButtons = () => {
        if (!this.state.buttonsWrapped) {
            [...this.elements.level2Menus].forEach((ele) => {
                if (ele.getBoundingClientRect().right > window.innerWidth) {
                    const lvl2Items = [
                        ...ele.querySelectorAll(`.${componentName}__level-2_button-label`),
                        ...ele.querySelectorAll(`.${componentName}__level-2_link-label`)
                    ];
                    lvl2Items.forEach((item) => {
                        const charArray = item.textContent?.trim().split('');
                        if (charArray && !charArray.includes('\n')) {
                            const midPoint = charArray.length / 2;
                            let middleSpaceIdx = 0;
                            charArray.forEach((char, idx) => {
                                if (char === ' ' && (Math.abs(midPoint - idx) <= Math.abs(midPoint - middleSpaceIdx))) {
                                    middleSpaceIdx = idx;
                                }
                            });
                            if (middleSpaceIdx !== 0) {
                                charArray[middleSpaceIdx] = '\n';
                                // eslint-disable-next-line no-param-reassign
                                item.textContent = charArray.join('');
                            }
                        }
                    });
                    this.state.buttonsWrapped = true;
                }
            });
        }
    };

    /**
     * On Window Scroll
     */
    onWindowScroll = () => {
        const globalNavHeight = document.querySelector('.ra-global-nav-v2__main-header, .plex-global-nav__main-header')?.getBoundingClientRect()?.height;

        if (globalNavHeight !== null || globalNavHeight !== undefined) {
            const subnavTop = document.querySelector('.sub-nav').getBoundingClientRect().top;
            let globalNavOffset = 0;
            if (subnavTop < globalNavHeight) {
                globalNavOffset = subnavTop - globalNavHeight;
            }
            document.querySelector('body').style.setProperty('--global-nav-offset', `${globalNavOffset}px`);
        }
    };

    /**
     * On Window Resize
     */
    onWindowResize = () => {
        this.currentViewport = getViewportSize(true);

        if(window.innerWidth !== this.currentWindowWidth) {
            this.updatePositions();
            this.closeAll();
            this.wrapButtons();
        }
        this.currentWindowWidth = window.innerWidth;
    };

    /**
     * Add Event Listeners
     */
    addEventListeners = () => {
        // Window
        window.addEventListener('resize', throttle(this.onWindowResize, this.resizeThrottleMs));
        window.addEventListener('scroll', this.onWindowScroll);
        window.addEventListener('global-nav-desktop-l2-open', this.setLowZindex);
        window.addEventListener('global-nav-desktop-l2-closed', this.removeZindex);
        document.addEventListener('click', this.onClickOff);

        // Component
        this.elements.mobileTrigger.addEventListener('click', this.toggleMobileSubNav);
        [...this.elements.level2Buttons].map((ele) => ele.addEventListener('click', this.onL2ButtonClick));
        [...this.elements.level3Buttons].map((ele) => ele.addEventListener('click', this.onL3ButtonClick));
        [...this.elements.subnavLinks].map((ele) => ele.addEventListener('click', this.scrollToInPageLink));
    };

    /**
     * Initialize Component
     */
    init() {
        // Disable JS for Authoring
        if(isEditModeFooter()) return;

        // Fire Init Funcs
        this.createSubNavGhost();
        this.updateMenuSizes();
        this.closeAll();
        this.addEventListeners();
        this.updateCurrentPageLink();
        this.wrapButtons();
    }
}

registerComponent(`.${componentName}`, SubNav);
